import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import moment from 'moment-timezone';
import {
  Search,
  FilterList,
  FileDownload,
  Edit,
  CheckCircle,
  Block,
  CalendarMonth,
  Today,
  Clear,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../components/Page';
import { getUsers, fetchDashboardOverview } from '../store/actions/users';
import { fCurrency } from '../utils/formatNumber';

const getSortLabel = (sortBy) => {
  if (sortBy === 'availableBalance') return 'Wallet Balance';
  if (sortBy === 'commissions') return 'Commission';
  return sortBy;
};

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color = '#10B981' }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 3 },
      borderRadius: '16px',
      bgcolor: '#FFFFFF',
      border: '1px solid #F3F4F6',
      boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography
          variant="body2"
          sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          {title}
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: '#111827',
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
          }}
        >
          {value ?? 0}
        </Typography>
      </Box>
      <Box
        sx={{
          p: { xs: 1, sm: 1.5 },
          borderRadius: '14px',
          bgcolor: alpha(color, 0.08),
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: { xs: 22, sm: 28 } }} />
      </Box>
    </Stack>
  </Paper>
);

StatCard.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.elementType,
  color: PropTypes.string,
};

export default function UserManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ─── Applied Filter State ────────────────────────────────────────────────
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    vipStatus: '',
    kycStatus: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  // ─── Temporary Modal Filter State (Only committed on Apply) ───────────────
  const [tempFilters, setTempFilters] = useState({
    status: '',
    vipStatus: '',
    kycStatus: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  const [openFilterModal, setOpenFilterModal] = useState(false);

  const { users, usersTotalCount, usersTotalPages, isLoading, overview } = useSelector((state) => state.users);

  // ─── 500ms Search Debounce ────────────────────────────────────────────────
  const searchTimerRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
  };

  // ─── Fetch Users with Applied Filters & Debounced Search ─────────────────
  const fetchUsersData = useCallback(
    (pageNumber = 1) => {
      const params = {
        page: pageNumber,
        limit: 10,
      };
      if (debouncedSearch && debouncedSearch.trim() !== '') {
        params.searchQuery = debouncedSearch.trim();
      }
      if (appliedFilters.status) {
        params.status = appliedFilters.status;
      }
      if (appliedFilters.vipStatus !== '') {
        params.vipStatus = appliedFilters.vipStatus;
      }
      if (appliedFilters.kycStatus !== '') {
        params.kycStatus = appliedFilters.kycStatus;
      }
      if (appliedFilters.startDate) {
        params.startDate = appliedFilters.startDate;
      }
      if (appliedFilters.endDate) {
        params.endDate = appliedFilters.endDate;
      }
      if (appliedFilters.sortBy) {
        params.sortBy = appliedFilters.sortBy;
      }
      if (appliedFilters.sortOrder) {
        params.sortOrder = appliedFilters.sortOrder;
      }

      dispatch(getUsers(params));
    },
    [dispatch, debouncedSearch, appliedFilters]
  );

  useEffect(() => {
    fetchUsersData(page);
  }, [fetchUsersData, page]);

  useEffect(() => {
    dispatch(fetchDashboardOverview());
  }, [dispatch]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Open Modal & sync temporary filter state with currently applied filters
  const handleOpenFilterModal = () => {
    setTempFilters({ ...appliedFilters });
    setOpenFilterModal(true);
  };

  // Apply filters ONLY when user clicks Apply
  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setOpenFilterModal(false);
    setPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    const defaultFilters = {
      status: '',
      vipStatus: '',
      kycStatus: '',
      startDate: '',
      endDate: '',
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    };
    setTempFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setOpenFilterModal(false);
    setPage(1);
  };

  const removeSingleFilter = (key) => {
    setAppliedFilters((prev) => ({ ...prev, [key]: '' }));
    setTempFilters((prev) => ({ ...prev, [key]: '' }));
    setPage(1);
  };

  const handleSortColumn = (columnKey) => {
    setAppliedFilters((prev) => {
      const isSame = prev.sortBy === columnKey;
      const newOrder = isSame && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC';
      return {
        ...prev,
        sortBy: columnKey,
        sortOrder: newOrder,
      };
    });
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    appliedFilters.status ||
      appliedFilters.vipStatus !== '' ||
      appliedFilters.kycStatus !== '' ||
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.sortBy !== 'createdAt' ||
      appliedFilters.sortOrder !== 'DESC'
  );

  return (
    <Page title="User Management">
      <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100vh', pb: 8, pt: { xs: 2, sm: 4 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Header Section */}
          <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#111827',
                letterSpacing: '-0.03em',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
              }}
            >
              User Management
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280', mt: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Manage your users and account status here
            </Typography>
          </Box>

          {/* Top Summary Cards Grid */}
          <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Total Active Users"
                value={overview?.activeUsersCount ?? 0}
                icon={CheckCircle}
                color="#10B981"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Total Inactive Users"
                value={overview?.inactiveUsersCount ?? 0}
                icon={Block}
                color="#EF4444"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Total Joined This Month"
                value={overview?.joinedThisMonthCount ?? 0}
                icon={CalendarMonth}
                color="#3B82F6"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Total Joined Today"
                value={overview?.joinedTodayCount ?? 0}
                icon={Today}
                color="#8B5CF6"
              />
            </Grid>
          </Grid>

          {/* Table / Cards Container Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            {/* Search & Filter Control Bar */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
              sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #F3F4F6' }}
            >
              <TextField
                placeholder="Search users by name, email, phone, or username..."
                size="small"
                fullWidth
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <Clear sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  maxWidth: { sm: 420 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                    bgcolor: '#FAFAFA',
                    fontSize: '0.9rem',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                  },
                }}
              />

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button
                  variant={hasActiveFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterList sx={{ fontSize: 18 }} />}
                  onClick={handleOpenFilterModal}
                  sx={{
                    borderRadius: '20px',
                    borderColor: '#E5E7EB',
                    bgcolor: hasActiveFilters ? '#111827' : '#FFFFFF',
                    color: hasActiveFilters ? '#FFFFFF' : '#374151',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    px: 2.2,
                    py: 0.6,
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      bgcolor: hasActiveFilters ? '#1F2937' : '#F9FAFB',
                    },
                  }}
                >
                  Filter {hasActiveFilters ? '(Active)' : ''}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: '20px',
                    borderColor: '#E5E7EB',
                    color: '#374151',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    px: 2,
                    py: 0.6,
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ p: 2, pt: 1, borderBottom: '1px solid #F3F4F6', bgcolor: '#F9FAFB' }}
              >
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                  Active Filters:
                </Typography>
                {appliedFilters.status && (
                  <Chip
                    label={`Status: ${appliedFilters.status.toUpperCase()}`}
                    size="small"
                    onDelete={() => removeSingleFilter('status')}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
                {appliedFilters.vipStatus !== '' && (
                  <Chip
                    label={`VIP: ${appliedFilters.vipStatus === 'true' ? 'VIP Only' : 'Normal Only'}`}
                    size="small"
                    onDelete={() => removeSingleFilter('vipStatus')}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
                {appliedFilters.kycStatus !== '' && (
                  <Chip
                    label={`KYC: ${appliedFilters.kycStatus === 'true' ? 'Verified' : 'Unverified'}`}
                    size="small"
                    onDelete={() => removeSingleFilter('kycStatus')}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
                {appliedFilters.startDate && (
                  <Chip
                    label={`From: ${appliedFilters.startDate}`}
                    size="small"
                    onDelete={() => removeSingleFilter('startDate')}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
                {appliedFilters.endDate && (
                  <Chip
                    label={`To: ${appliedFilters.endDate}`}
                    size="small"
                    onDelete={() => removeSingleFilter('endDate')}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
                {(appliedFilters.sortBy !== 'createdAt' || appliedFilters.sortOrder !== 'DESC') && (
                  <Chip
                    label={`Sorted By: ${getSortLabel(appliedFilters.sortBy)} (${appliedFilters.sortOrder})`}
                    size="small"
                    onDelete={() => {
                      setAppliedFilters((prev) => ({ ...prev, sortBy: 'createdAt', sortOrder: 'DESC' }));
                      setTempFilters((prev) => ({ ...prev, sortBy: 'createdAt', sortOrder: 'DESC' }));
                    }}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem', bgcolor: '#EEF2FF', color: '#4F46E5' }}
                  />
                )}
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 1 }}
                >
                  Clear All
                </Button>
              </Stack>
            )}

            {/* Mobile View: Render Cards for small screens */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, p: { xs: 1.5, sm: 2 } }}>
              {(() => {
                if (isLoading) {
                  return (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress size={30} />
                    </Box>
                  );
                }
                if (!users || users.length === 0) {
                  return (
                    <Typography variant="body2" align="center" sx={{ color: '#6B7280', py: 4 }}>
                      No users found matching your query.
                    </Typography>
                  );
                }
                return users.map((item) => {
                  const isVip = item.vip_status === true || item.vip_status === 'vip' || item.vip_status === 'true';
                  const st = (item.status || 'active').toLowerCase();
                  let bgColor = '#D1FAE5';
                  let textColor = '#10B981';
                  if (st === 'inactive' || st === 'suspend' || st === 'block') {
                    bgColor = '#FEE2E2';
                    textColor = '#DC2626';
                  } else if (st === 'pending') {
                    bgColor = '#FEF3C7';
                    textColor = '#D97706';
                  }

                  const displayName =
                    `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || 'User';

                  const walletBal = item.Wallet?.availableBalance ?? item.availableBalance ?? 0;
                  const commBal = item.Wallet?.commissions ?? item.commissions ?? 0;

                  return (
                    <Paper
                      key={item.id}
                      elevation={0}
                      onClick={() => navigate(`/dashboard/user-details/${item.id}`)}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#5D5FEF',
                          boxShadow: '0 4px 12px rgba(93,95,239,0.08)',
                        },
                      }}
                    >
                      {/* Top Section */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1.5}
                        sx={{ mb: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: '#5D5FEF',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              flexShrink: 0,
                              mt: 0.2,
                            }}
                          >
                            {displayName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#111827', fontSize: '0.875rem', lineHeight: 1.2 }}
                            >
                              {displayName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#6B7280',
                                display: 'block',
                                wordBreak: 'break-all',
                                fontSize: '0.75rem',
                                mt: 0.3,
                                lineHeight: 1.25,
                              }}
                            >
                              {item.email || '—'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="column" spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                          <Chip
                            label={st.toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.625rem',
                              height: 20,
                              bgcolor: bgColor,
                              color: textColor,
                              borderRadius: '6px',
                              px: 0.5,
                            }}
                          />
                          {isVip && (
                            <Chip
                              label="VIP"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.625rem',
                                height: 20,
                                bgcolor: '#F3E8FF',
                                color: '#9333EA',
                                borderRadius: '6px',
                                px: 0.5,
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>

                      {/* Middle Financial Stats Bar */}
                      <Grid container spacing={1} sx={{ mb: 1.5, p: 1, bgcolor: '#F9FAFB', borderRadius: '8px' }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, display: 'block', fontSize: '0.675rem' }}>
                            Wallet Balance
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#059669', fontSize: '0.8rem' }}>
                            ₦{fCurrency(walletBal)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, display: 'block', fontSize: '0.675rem' }}>
                            Commission
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#D97706', fontSize: '0.8rem' }}>
                            ₦{fCurrency(commBal)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Bottom Section */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ pt: 1.2, borderTop: '1px border-dashed #F3F4F6' }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#1D4ED8',
                              fontWeight: 700,
                              display: 'block',
                              fontSize: '0.75rem',
                              wordBreak: 'break-all',
                            }}
                          >
                            @{item.username || '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#6B7280', display: 'block', fontSize: '0.725rem' }}
                          >
                            {item.phone || 'No phone'} • Joined:{' '}
                            {item.createdAt ? moment(item.createdAt).format('DD MMM YYYY') : '—'}
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit sx={{ fontSize: 14 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/user-details/${item.id}`);
                          }}
                          sx={{
                            borderRadius: '10px',
                            borderColor: '#E5E7EB',
                            color: '#374151',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            px: 1.5,
                            py: 0.4,
                            flexShrink: 0,
                          }}
                        >
                          Details
                        </Button>
                      </Stack>
                    </Paper>
                  );
                });
              })()}
            </Box>

            {/* Desktop View: User Data Table for medium screens & up */}
            <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1250 }}>
                <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                  <TableRow>
                    <TableCell
                      onClick={() => handleSortColumn('firstName')}
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active={appliedFilters.sortBy === 'firstName'}
                        direction={appliedFilters.sortBy === 'firstName' ? appliedFilters.sortOrder.toLowerCase() : 'desc'}
                      >
                        First Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      Last Name
                    </TableCell>
                    <TableCell
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      onClick={() => handleSortColumn('username')}
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active={appliedFilters.sortBy === 'username'}
                        direction={appliedFilters.sortBy === 'username' ? appliedFilters.sortOrder.toLowerCase() : 'desc'}
                      >
                        Username
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      Phone
                    </TableCell>
                    <TableCell
                      onClick={() => handleSortColumn('availableBalance')}
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active={appliedFilters.sortBy === 'availableBalance'}
                        direction={appliedFilters.sortBy === 'availableBalance' ? appliedFilters.sortOrder.toLowerCase() : 'desc'}
                      >
                        Wallet Balance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSortColumn('commissions')}
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active={appliedFilters.sortBy === 'commissions'}
                        direction={appliedFilters.sortBy === 'commissions' ? appliedFilters.sortOrder.toLowerCase() : 'desc'}
                      >
                        Commission
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      VIP Status
                    </TableCell>
                    <TableCell
                      onClick={() => handleSortColumn('createdAt')}
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active={appliedFilters.sortBy === 'createdAt'}
                        direction={appliedFilters.sortBy === 'createdAt' ? appliedFilters.sortOrder.toLowerCase() : 'desc'}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.8rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(() => {
                    if (isLoading) {
                      return (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                            <CircularProgress size={32} />
                          </TableCell>
                        </TableRow>
                      );
                    }
                    if (!users || users.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 6, color: '#6B7280' }}>
                            No users found matching your query.
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return users.map((item) => {
                      const isVip = item.vip_status === true || item.vip_status === 'vip' || item.vip_status === 'true';
                      const walletBal = item.Wallet?.availableBalance ?? item.availableBalance ?? 0;
                      const commBal = item.Wallet?.commissions ?? item.commissions ?? 0;

                      return (
                        <TableRow
                          key={item.id}
                          hover
                          onClick={() => navigate(`/dashboard/user-details/${item.id}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:hover': { bgcolor: '#F9FAFB' },
                          }}
                        >
                          {/* First Name */}
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: '#5D5FEF',
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {(item.firstName || item.username || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                                {item.firstName || '—'}
                              </Typography>
                            </Stack>
                          </TableCell>

                          {/* Last Name */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                              {item.lastName || '—'}
                            </Typography>
                          </TableCell>

                          {/* Email */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#4B5563' }}>
                              {item.email || '—'}
                            </Typography>
                          </TableCell>

                          {/* Username */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D4ED8' }}>
                              @{item.username || '—'}
                            </Typography>
                          </TableCell>

                          {/* Phone */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#4B5563' }}>
                              {item.phone || '—'}
                            </Typography>
                          </TableCell>

                          {/* Wallet Balance */}
                          <TableCell sx={{ py: 2, whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                              ₦{fCurrency(walletBal)}
                            </Typography>
                          </TableCell>

                          {/* Commission Balance */}
                          <TableCell sx={{ py: 2, whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#D97706' }}>
                              ₦{fCurrency(commBal)}
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={{ py: 2 }}>
                            {(() => {
                              const st = (item.status || 'active').toLowerCase();
                              let bgColor = '#D1FAE5';
                              let textColor = '#10B981';
                              if (st === 'inactive' || st === 'suspend' || st === 'block') {
                                bgColor = '#FEE2E2';
                                textColor = '#DC2626';
                              } else if (st === 'pending') {
                                bgColor = '#FEF3C7';
                                textColor = '#D97706';
                              }
                              return (
                                <Chip
                                  label={st.toUpperCase()}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.725rem',
                                    bgcolor: bgColor,
                                    color: textColor,
                                    borderRadius: '12px',
                                    px: 0.5,
                                  }}
                                />
                              );
                            })()}
                          </TableCell>

                          {/* VIP Status */}
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={isVip ? 'VIP' : 'NORMAL'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.725rem',
                                bgcolor: isVip ? '#F3E8FF' : '#F3F4F6',
                                color: isVip ? '#9333EA' : '#6B7280',
                                borderRadius: '12px',
                                px: 0.5,
                              }}
                            />
                          </TableCell>

                          {/* Created At */}
                          <TableCell sx={{ py: 2, whiteSpace: 'nowrap' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#4B5563', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                            >
                              {item.createdAt ? moment(item.createdAt).format('DD MMMM YYYY, hh:mm A') : '—'}
                            </Typography>
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Edit sx={{ fontSize: 16 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/user-details/${item.id}`);
                                }}
                                sx={{
                                  borderRadius: '16px',
                                  borderColor: '#E5E7EB',
                                  color: '#374151',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  px: 1.8,
                                  py: 0.4,
                                }}
                              >
                                Details
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {usersTotalPages > 1 && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
                sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid #F3F4F6' }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#6B7280', fontSize: '0.85rem', textAlign: { xs: 'center', sm: 'left' } }}
                >
                  Showing Page {page} of {usersTotalPages} ({usersTotalCount || 0} total users)
                </Typography>
                <Pagination
                  count={usersTotalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '8px',
                      fontWeight: 600,
                    },
                  }}
                />
              </Stack>
            )}
          </Paper>
        </Container>

        {/* Filter Modal */}
        <Dialog open={openFilterModal} onClose={() => setOpenFilterModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Filter & Sort Users</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={tempFilters.status}
                  label="Account Status"
                  onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspend">Suspended</MenuItem>
                  <MenuItem value="block">Blocked</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>VIP Status</InputLabel>
                <Select
                  value={tempFilters.vipStatus}
                  label="VIP Status"
                  onChange={(e) => setTempFilters({ ...tempFilters, vipStatus: e.target.value })}
                >
                  <MenuItem value="">All Users</MenuItem>
                  <MenuItem value="true">VIP Only</MenuItem>
                  <MenuItem value="false">Normal Only</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>KYC Verification</InputLabel>
                <Select
                  value={tempFilters.kycStatus}
                  label="KYC Verification"
                  onChange={(e) => setTempFilters({ ...tempFilters, kycStatus: e.target.value })}
                >
                  <MenuItem value="">All Users</MenuItem>
                  <MenuItem value="true">Verified Only</MenuItem>
                  <MenuItem value="false">Unverified Only</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={tempFilters.sortBy}
                    label="Sort By"
                    onChange={(e) => setTempFilters({ ...tempFilters, sortBy: e.target.value })}
                  >
                    <MenuItem value="createdAt">Date Joined</MenuItem>
                    <MenuItem value="availableBalance">Wallet Balance</MenuItem>
                    <MenuItem value="commissions">Commission Balance</MenuItem>
                    <MenuItem value="firstName">First Name</MenuItem>
                    <MenuItem value="username">Username</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Order</InputLabel>
                  <Select
                    value={tempFilters.sortOrder}
                    label="Order"
                    onChange={(e) => setTempFilters({ ...tempFilters, sortOrder: e.target.value })}
                  >
                    <MenuItem value="DESC">Newest / Z-A</MenuItem>
                    <MenuItem value="ASC">Oldest / A-Z</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={1.5}>
                <TextField
                  label="Joined From"
                  type="date"
                  size="small"
                  value={tempFilters.startDate}
                  onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Joined To"
                  type="date"
                  size="small"
                  value={tempFilters.endDate}
                  onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleResetFilters} color="inherit">
              Reset
            </Button>
            <Button
              onClick={handleApplyFilters}
              variant="contained"
              sx={{ bgcolor: '#111827', '&:hover': { bgcolor: '#1F2937' } }}
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
